import React, { useState, useEffect, useContext } from 'react';
import {
  XMarkIcon,
  CurrencyEuroIcon,
  CalendarIcon,
  TagIcon,
  UserIcon,
  DocumentPlusIcon,
  ArrowsRightLeftIcon,
  BuildingLibraryIcon,
  WalletIcon,
  ArrowDownIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';

interface AddBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface Category {
  id: number;
  name: string;
}

interface Member {
  member_id: string;
  vorname: string;
  name: string;
}

interface Account {
  id: number;
  name: string;
  kontotyp: string;
}

const AddBookingModal: React.FC<AddBookingModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const [type, setType] = useState<'deposit' | 'withdrawal' | 'transfer'>('deposit');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [categoryId, setCategoryId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [targetAccountId, setTargetAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [invoice, setInvoice] = useState<File | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const [categoriesRes, membersRes, accountsRes] = await Promise.all([
        apiClient.get('/api/finance/categories'),
        apiClient.get('/api/finance/members'),
        apiClient.get('/api/finance/accounts')
      ]);
      
      setCategories(categoriesRes.data);
      setMembers(membersRes.data);
      setAccounts(accountsRes.data);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Daten konnten nicht geladen werden.',
        variant: 'single'
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Datei zu groß',
        message: 'Die Datei darf maximal 5MB groß sein.',
        variant: 'single'
      });
      return;
    }
    
    if (!file.type.match(/^(application\/pdf|image\/(jpg|jpeg|png))$/)) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Ungültiges Format',
        message: 'Nur PDF-, JPG-, JPEG- oder PNG-Dateien sind erlaubt.',
        variant: 'single'
      });
      return;
    }
    
    setInvoice(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validierung
    if (!type || !amount || !date) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Pflichtfelder fehlen',
        message: 'Bitte füllen Sie alle Pflichtfelder aus (Typ, Betrag, Datum).',
        variant: 'single'
      });
      return;
    }
    
    if (type !== 'transfer' && !categoryId) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Kategorie fehlt',
        message: 'Bitte wählen Sie eine Kategorie für die Buchung.',
        variant: 'single'
      });
      return;
    }
    
    if (type === 'transfer' && (!sourceAccountId || !targetAccountId || sourceAccountId === targetAccountId)) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Konten ungültig',
        message: 'Bitte wählen Sie unterschiedliche Quell- und Zielkonten.',
        variant: 'single'
      });
      return;
    }

    setLoading(true);
    
    const formData = new FormData();
    formData.append('type', type);
    // Betrag immer als positiver Wert senden
    formData.append('amount', Math.abs(parseFloat(amount)).toString());
    formData.append('date', date);
    
    if (categoryId) {
      formData.append('category_id', categoryId);
    }
    if (memberId) {
      formData.append('member_id', memberId);
    }
    if (type !== 'transfer') {
      formData.append('account_id', sourceAccountId || '');
    } else {
      formData.append('source_account_id', sourceAccountId);
      formData.append('target_account_id', targetAccountId);
    }
    formData.append('description', description || '');
    if (invoice) {
      formData.append('documents', invoice);
    }

    try {
      await apiClient.post('/api/finance/bookings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Die Buchung wurde erfolgreich hinzugefügt.',
        variant: 'single'
      });
      
      setTimeout(() => {
        handleClose();
        resetForm();
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.error || err.message || 'Die Buchung konnte nicht gespeichert werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setType('deposit');
    setAmount('');
    setDate(new Date().toISOString().split('T')[0]);
    setCategoryId('');
    setMemberId('');
    setSourceAccountId('');
    setTargetAccountId('');
    setDescription('');
    setInvoice(null);
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      resetForm();
    }, 300);
  };

  const getAccountIcon = (kontotyp: string) => {
    switch (kontotyp) {
      case 'Girokonto':
        return <BuildingLibraryIcon className="h-4 w-4" />;
      case 'Bargeldkasse':
        return <WalletIcon className="h-4 w-4" />;
      default:
        return <CurrencyEuroIcon className="h-4 w-4" />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
        onClick={handleClose}
      >
        <div
          className="w-full sm:max-w-2xl bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
              <CurrencyEuroIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Neue Buchung</h3>
            </div>
            <button
              onClick={handleClose}
              className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Typ */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Typ <span className="text-error-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setType('deposit')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      type === 'deposit'
                        ? 'bg-success-50 dark:bg-success-900/20 border-success-500 text-success-700 dark:text-success-300'
                        : 'bg-gray-50 dark:bg-[#0f1419] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <ArrowDownIcon className="h-5 w-5 mx-auto mb-1 text-success-600" />
                    <span className="text-xs font-medium">Einzahlung</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('withdrawal')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      type === 'withdrawal'
                        ? 'bg-error-50 dark:bg-error-900/20 border-error-500 text-error-700 dark:text-error-300'
                        : 'bg-gray-50 dark:bg-[#0f1419] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <ArrowUpIcon className="h-5 w-5 mx-auto mb-1 text-error-600" />
                    <span className="text-xs font-medium">Auszahlung</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('transfer')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      type === 'transfer'
                        ? 'bg-info-50 dark:bg-info-900/20 border-info-500 text-info-700 dark:text-info-300'
                        : 'bg-gray-50 dark:bg-[#0f1419] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <ArrowsRightLeftIcon className="h-5 w-5 mx-auto mb-1 text-info-600" />
                    <span className="text-xs font-medium">Übertrag</span>
                  </button>
                </div>
              </div>

              {/* Betrag und Datum */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Betrag (€) <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <CurrencyEuroIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAmount(value);

                        // Automatische Typ-Erkennung
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue < 0) {
                          setType('withdrawal');
                        } else if (!isNaN(numValue) && numValue > 0 && type === 'withdrawal') {
                          setType('deposit');
                        }
                      }}
                      required
                      placeholder="0,00"
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Datum <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Kategorie (nicht bei Transfer) */}
              {type !== 'transfer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kategorie <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <select
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                      required
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
                      disabled={loading}
                    >
                      <option value="">Kategorie wählen</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Mitglied */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mitglied (optional)
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    value={memberId}
                    onChange={(e) => setMemberId(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
                    disabled={loading}
                  >
                    <option value="">Kein Mitglied</option>
                    {members.map((member) => (
                      <option key={member.member_id} value={member.member_id}>
                        {member.vorname} {member.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Konten */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {type === 'transfer' ? 'Quellkonto' : 'Konto'} <span className="text-error-500">*</span>
                  </label>
                  <select
                    value={sourceAccountId}
                    onChange={(e) => setSourceAccountId(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
                    disabled={loading}
                  >
                    <option value="">Konto wählen</option>
                    {accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        <span className="flex items-center gap-2">
                          {acc.name}
                        </span>
                      </option>
                    ))}
                  </select>
                </div>

                {type === 'transfer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Zielkonto <span className="text-error-500">*</span>
                    </label>
                    <select
                      value={targetAccountId}
                      onChange={(e) => setTargetAccountId(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white appearance-none"
                      disabled={loading}
                    >
                      <option value="">Konto wählen</option>
                      {accounts.filter(acc => acc.id.toString() !== sourceAccountId).map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Beschreibung */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optionale Notizen zur Buchung"
                  className="w-full px-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                  rows={3}
                  disabled={loading}
                />
              </div>

              {/* Datei Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Beleg hochladen (optional)
                </label>
                <div className="relative">
                  <DocumentPlusIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="file"
                    accept="application/pdf,image/jpg,image/jpeg,image/png"
                    onChange={handleFileChange}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white file:mr-4 file:py-1.5 file:px-4 file:rounded-xl file:border-0 file:bg-primary-600 file:text-white file:hover:bg-primary-700 file:transition-colors"
                    disabled={loading}
                  />
                </div>
                {invoice && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Ausgewählte Datei: {invoice.name}
                  </p>
                )}
              </div>

            </form>
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-sm font-semibold"
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => handleSubmit({ preventDefault: () => {} } as any)}
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Wird gespeichert...' : 'Buchung hinzufügen'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'single'}
        showCloseButton={true}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
        } : undefined}
      />
    </>
  );
};

export default AddBookingModal;